import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  PlayIcon,
  PauseCircleIcon,
  VolumeMaxIcon,
  VolumeXIcon,
  Download01Icon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'
import { Slider } from '@/components/ui/Slider'

export interface AudioPlayerProps {
  src: string
  fileName?: string
  twilioId?: string
  className?: string
  showDownload?: boolean
  showVolume?: boolean
  showSpeed?: boolean
}

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const

export function AudioPlayer({
  src,
  fileName,
  twilioId,
  className,
  showDownload = true,
  showVolume = true,
  showSpeed = true,
}: AudioPlayerProps) {
  const { t } = useTranslation()
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [isDragging, setIsDragging] = useState(false)
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const volumeHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleVolumeMouseEnter = useCallback(() => {
    if (volumeHideTimeoutRef.current) {
      clearTimeout(volumeHideTimeoutRef.current)
      volumeHideTimeoutRef.current = null
    }
    setShowVolumeSlider(true)
  }, [])

  const handleVolumeMouseLeave = useCallback(() => {
    volumeHideTimeoutRef.current = setTimeout(() => {
      setShowVolumeSlider(false)
    }, 150)
  }, [])

  const formatTime = (time: number): string => {
    if (!isFinite(time) || isNaN(time)) {
      return '0:00'
    }
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current && !isDragging) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }, [isDragging])

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current && isFinite(audioRef.current.duration)) {
      setDuration(audioRef.current.duration)
    }
  }, [])

  const handleSeek = useCallback(
    (value: number) => {
      setCurrentTime(value)
      if (audioRef.current) {
        audioRef.current.currentTime = value
      }
    },
    []
  )

  const handleVolumeChange = useCallback((value: number) => {
    const volumeValue = value / 100
    setVolume(volumeValue)
    setIsMuted(volumeValue === 0)
    if (audioRef.current) {
      audioRef.current.volume = volumeValue
    }
  }, [])

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 1
        setIsMuted(false)
      } else {
        audioRef.current.volume = 0
        setIsMuted(true)
      }
    }
  }, [isMuted, volume])

  const cycleSpeed = useCallback(() => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed as typeof PLAYBACK_SPEEDS[number])
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length
    const newSpeed = PLAYBACK_SPEEDS[nextIndex]
    setPlaybackSpeed(newSpeed)
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed
    }
  }, [playbackSpeed])

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(src)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || `conversation-${twilioId || 'audio'}.wav`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading file:', error)
    }
  }, [src, fileName, twilioId])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleEnded = () => setIsPlaying(false)
    const handleError = () => {
      setDuration(0)
      setCurrentTime(0)
    }

    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('error', handleError)

    return () => {
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('error', handleError)
    }
  }, [])

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3',
        'dark:border-gray-700 dark:bg-gray-900',
        className
      )}
    >
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        preload="metadata"
      />

      {/* Play/Pause button */}
      <Button
        color="primary"
        size="sm"
        onPress={handlePlayPause}
        className="h-8 w-8 rounded-full p-0"
        iconLeading={isPlaying ? PauseCircleIcon : PlayIcon}
      />

      {/* Time display */}
      <div className="text-sm text-gray-600 dark:text-gray-400 min-w-[70px]">
        {formatTime(currentTime)} / {formatTime(duration)}
      </div>

      {/* Progress slider */}
      <div className="flex-1">
        <Slider
          value={currentTime}
          minValue={0}
          maxValue={duration || 100}
          onChange={handleSeek}
          onChangeEnd={() => setIsDragging(false)}
          aria-label={t('components.audio_player.audio_progress')}
        />
      </div>

      {/* Speed button */}
      {showSpeed && (
        <button
          onClick={cycleSpeed}
          className="text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 min-w-[40px]"
        >
          {playbackSpeed}x
        </button>
      )}

      {/* Volume control */}
      {showVolume && (
        <div
          className="relative"
          onMouseEnter={handleVolumeMouseEnter}
          onMouseLeave={handleVolumeMouseLeave}
        >
          <Button
            color="tertiary"
            size="sm"
            onPress={toggleMute}
            className="p-1"
            iconLeading={isMuted || volume === 0 ? VolumeXIcon : VolumeMaxIcon}
          />

          {showVolumeSlider && (
            <div
              className="absolute bottom-full left-1/2 -translate-x-1/2 pb-3"
              onMouseEnter={handleVolumeMouseEnter}
              onMouseLeave={handleVolumeMouseLeave}
            >
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="w-24">
                  <Slider
                    value={isMuted ? 0 : volume * 100}
                    minValue={0}
                    maxValue={100}
                    onChange={handleVolumeChange}
                    aria-label={t('components.audio_player.volume')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Download button */}
      {showDownload && (
        <Button color="tertiary" size="sm" onPress={handleDownload} className="p-1" iconLeading={Download01Icon} />
      )}
    </div>
  )
}

export default AudioPlayer
